import { useState, useEffect } from "react";
import { useApp } from "@/contexts/AppContext";
import api, { formatCurrency, formatDate } from "@/lib/api";
import { Plus, Play, FileText, ChevronDown, ChevronUp } from "lucide-react";

export default function LeasesPage() {
  const { activeEntity, entities } = useApp();
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState({
    lease_name: "", asset_description: "", lessor: "", entity_id: "",
    commencement_date: new Date().toISOString().substring(0, 10), end_date: "",
    lease_term_months: 36, monthly_payment: 0, annual_escalation_pct: 0,
    discount_rate: 10, initial_direct_costs: 0, restoration_cost: 0, notes: ""
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeEntity) params.entity_id = activeEntity.id;
      const { data } = await api.get("/leases", { params });
      setLeases(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [activeEntity]);

  const createLease = async () => {
    try {
      const payload = { ...form, entity_id: form.entity_id || activeEntity?.id };
      await api.post("/leases", payload);
      setShowForm(false);
      setForm({ lease_name: "", asset_description: "", lessor: "", entity_id: "", commencement_date: new Date().toISOString().substring(0, 10), end_date: "", lease_term_months: 36, monthly_payment: 0, annual_escalation_pct: 0, discount_rate: 10, initial_direct_costs: 0, restoration_cost: 0, notes: "" });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  const runMonth = async (leaseId, leaseName) => {
    const period = prompt("Period (YYYY-MM):", new Date().toISOString().substring(0, 7));
    if (!period) return;
    try {
      const { data } = await api.post(`/leases/${leaseId}/run-month`, { period });
      alert(`Processed:\n- ROU amortisation: ${formatCurrency(data.amortisation_journal.total_debit)}\n- Interest: ${formatCurrency(data.interest)}\n- Principal: ${formatCurrency(data.principal)}\n- Remaining liability: ${formatCurrency(data.lease_liability)}`);
      load();
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  // Compute totals
  const totalROU = leases.reduce((s, l) => s + (l.rou_carrying || 0), 0);
  const totalLiab = leases.reduce((s, l) => s + (l.lease_liability_current || 0), 0);
  const totalMonthly = leases.reduce((s, l) => s + (l.monthly_payment || 0), 0);

  return (
    <div className="space-y-6" data-testid="leases-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0B1020]">
            <FileText size={24} className="inline mr-2 text-[#C8A961]" />
            IFRS 16 Leases
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">Right-of-use assets, lease liabilities, amortisation & interest unwinding</p>
        </div>
        <button onClick={() => setShowForm(true)} className="stratum-btn-primary flex items-center gap-2" data-testid="new-lease-btn">
          <Plus size={16} /> New Lease
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="stratum-card p-5">
          <span className="text-xs text-[#6B7280] uppercase tracking-wider">ROU Assets (Carrying)</span>
          <div className="font-mono text-xl font-semibold text-[#0B1020] tabular-nums mt-2">{formatCurrency(totalROU)}</div>
        </div>
        <div className="stratum-card p-5">
          <span className="text-xs text-[#6B7280] uppercase tracking-wider">Lease Liabilities</span>
          <div className="font-mono text-xl font-semibold text-[#991B1B] tabular-nums mt-2">{formatCurrency(totalLiab)}</div>
        </div>
        <div className="stratum-card p-5">
          <span className="text-xs text-[#6B7280] uppercase tracking-wider">Monthly Payments</span>
          <div className="font-mono text-xl font-semibold text-[#C8A961] tabular-nums mt-2">{formatCurrency(totalMonthly)}</div>
        </div>
        <div className="stratum-card p-5">
          <span className="text-xs text-[#6B7280] uppercase tracking-wider">Active Leases</span>
          <div className="font-mono text-xl font-semibold text-[#0B1020] tabular-nums mt-2">{leases.length}</div>
        </div>
      </div>

      {/* New Lease Form */}
      {showForm && (
        <div className="stratum-card p-6">
          <h3 className="font-display text-lg font-semibold mb-4">Recognise New Lease (IFRS 16)</h3>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs font-medium text-[#6B7280] mb-1">Lease Name *</label><input className="stratum-input w-full" value={form.lease_name} onChange={e => setForm({...form, lease_name: e.target.value})} data-testid="lease-name-input" /></div>
            <div><label className="block text-xs font-medium text-[#6B7280] mb-1">Lessor</label><input className="stratum-input w-full" value={form.lessor} onChange={e => setForm({...form, lessor: e.target.value})} /></div>
            <div><label className="block text-xs font-medium text-[#6B7280] mb-1">Asset Description</label><input className="stratum-input w-full" value={form.asset_description} onChange={e => setForm({...form, asset_description: e.target.value})} /></div>
            <div><label className="block text-xs font-medium text-[#6B7280] mb-1">Entity</label>
              <select className="stratum-input w-full" value={form.entity_id || activeEntity?.id || ""} onChange={e => setForm({...form, entity_id: e.target.value})}>
                {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div><label className="block text-xs font-medium text-[#6B7280] mb-1">Commencement Date</label><input type="date" className="stratum-input w-full" value={form.commencement_date} onChange={e => setForm({...form, commencement_date: e.target.value})} /></div>
            <div><label className="block text-xs font-medium text-[#6B7280] mb-1">Lease Term (months) *</label><input type="number" className="stratum-input w-full font-mono" value={form.lease_term_months} onChange={e => setForm({...form, lease_term_months: parseInt(e.target.value) || 0})} data-testid="lease-term-input" /></div>
            <div><label className="block text-xs font-medium text-[#6B7280] mb-1">Monthly Payment (R) *</label><input type="number" className="stratum-input w-full font-mono" value={form.monthly_payment} onChange={e => setForm({...form, monthly_payment: parseFloat(e.target.value) || 0})} data-testid="lease-payment-input" /></div>
            <div><label className="block text-xs font-medium text-[#6B7280] mb-1">Discount Rate (% annual) *</label><input type="number" step="0.1" className="stratum-input w-full font-mono" value={form.discount_rate} onChange={e => setForm({...form, discount_rate: parseFloat(e.target.value) || 0})} data-testid="lease-rate-input" /></div>
            <div><label className="block text-xs font-medium text-[#6B7280] mb-1">Initial Direct Costs (R)</label><input type="number" className="stratum-input w-full font-mono" value={form.initial_direct_costs} onChange={e => setForm({...form, initial_direct_costs: parseFloat(e.target.value) || 0})} /></div>
            <div><label className="block text-xs font-medium text-[#6B7280] mb-1">Restoration Cost (R)</label><input type="number" className="stratum-input w-full font-mono" value={form.restoration_cost} onChange={e => setForm({...form, restoration_cost: parseFloat(e.target.value) || 0})} /></div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg border border-[#E8E2D5] text-sm">Cancel</button>
            <button onClick={createLease} className="stratum-btn-primary" data-testid="create-lease-btn" disabled={!form.lease_name || !form.monthly_payment}>Create Lease</button>
          </div>
        </div>
      )}

      {/* Lease List */}
      {loading ? <div className="stratum-card p-8 text-center text-[#6B7280]">Loading...</div> :
       leases.length === 0 ? <div className="stratum-card p-12 text-center text-[#6B7280]">No leases. Add your first IFRS 16 lease to get started.</div> :
       <div className="space-y-3">
        {leases.map(l => (
          <div key={l.id} className="stratum-card overflow-hidden" data-testid={`lease-${l.id}`}>
            <div className="p-5 cursor-pointer hover:bg-[#FAF8F4]" onClick={() => setExpanded(expanded === l.id ? null : l.id)}>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-[#0B1020]">{l.lease_name}</h4>
                  <div className="flex gap-4 text-xs text-[#6B7280] mt-1">
                    <span>Lessor: {l.lessor || "N/A"}</span>
                    <span>{l.lease_term_months} months</span>
                    <span>Rate: {l.discount_rate}%</span>
                    <span>Payment: {formatCurrency(l.monthly_payment)}/mo</span>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="font-mono text-sm tabular-nums font-semibold">{formatCurrency(l.rou_carrying)}</div>
                    <div className="text-[10px] text-[#6B7280]">ROU Carrying</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm tabular-nums font-semibold text-[#991B1B]">{formatCurrency(l.lease_liability_current)}</div>
                    <div className="text-[10px] text-[#6B7280]">Liability</div>
                  </div>
                  {expanded === l.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </div>
            </div>
            {expanded === l.id && (
              <div className="border-t border-[#E8E2D5] p-5 bg-[#FAF8F4]/50 space-y-4">
                <div className="grid grid-cols-4 gap-3 text-sm">
                  <div><span className="text-[#6B7280] text-xs">ROU Initial</span><div className="font-mono tabular-nums">{formatCurrency(l.rou_asset_initial)}</div></div>
                  <div><span className="text-[#6B7280] text-xs">Accum Amort</span><div className="font-mono tabular-nums text-[#991B1B]">({formatCurrency(l.rou_accumulated_amort)})</div></div>
                  <div><span className="text-[#6B7280] text-xs">Monthly Amort</span><div className="font-mono tabular-nums">{formatCurrency(l.rou_monthly_amort)}</div></div>
                  <div><span className="text-[#6B7280] text-xs">Total Payments</span><div className="font-mono tabular-nums">{formatCurrency(l.total_payments_made)}</div></div>
                </div>
                {/* Schedule preview */}
                {l.amortisation_schedule?.length > 0 && (
                  <div className="overflow-x-auto max-h-48">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-[#0B1020] text-white"><th className="px-2 py-1">Month</th><th className="px-2 py-1 text-right">Payment</th><th className="px-2 py-1 text-right">Interest</th><th className="px-2 py-1 text-right">Principal</th><th className="px-2 py-1 text-right">Liability Bal</th><th className="px-2 py-1 text-right">ROU Amort</th><th className="px-2 py-1 text-right">ROU Bal</th></tr></thead>
                      <tbody>
                        {l.amortisation_schedule.slice(0, 12).map((s, i) => (
                          <tr key={i} className={i % 2 ? "bg-[#FAF8F4]" : "bg-white"}>
                            <td className="px-2 py-1 font-mono">{s.month}</td>
                            <td className="px-2 py-1 text-right font-mono tabular-nums">{formatCurrency(s.payment)}</td>
                            <td className="px-2 py-1 text-right font-mono tabular-nums text-[#991B1B]">{formatCurrency(s.interest)}</td>
                            <td className="px-2 py-1 text-right font-mono tabular-nums">{formatCurrency(s.principal)}</td>
                            <td className="px-2 py-1 text-right font-mono tabular-nums">{formatCurrency(s.liability_balance)}</td>
                            <td className="px-2 py-1 text-right font-mono tabular-nums">{formatCurrency(s.rou_amort)}</td>
                            <td className="px-2 py-1 text-right font-mono tabular-nums">{formatCurrency(s.rou_balance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {l.amortisation_schedule.length > 12 && <p className="text-xs text-[#6B7280] mt-1">...{l.amortisation_schedule.length - 12} more months</p>}
                  </div>
                )}
                <button onClick={() => runMonth(l.id, l.lease_name)} className="stratum-btn-primary flex items-center gap-2 text-sm" data-testid={`run-lease-${l.id}`}>
                  <Play size={14} /> Process Monthly (Amortise + Payment)
                </button>
              </div>
            )}
          </div>
        ))}
       </div>}
    </div>
  );
}
